import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  getAuth,
} from 'firebase/auth';
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db, firebaseConfig, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  checkLoginRestrictions,
  getStaffPermissions,
  mergeStaffRecordFields,
  applyClinicAdminPrivileges,
  resolveClinicNameFromFirestore,
} from '../utils/loginRestrictions';
import { syncUserClinicToFirestore } from '../utils/syncUserClinicToFirestore';
import { getCallableErrorMessage } from '../utils/serviceErrorMessage';
import {
  MfaRequiredError,
  completeTotpSignIn,
  isMfaRequiredError,
  mfaActionErrorMessage,
} from './superadminMfa';

const IMPERSONATION_SESSION_KEY = 'smileconsent_impersonation';

/**
 * Create a Firebase Auth email/password user without switching the current session.
 * Used when a super admin creates a clinic admin while staying signed in.
 */
async function createAuthUserWithoutSigningIn(email, password, displayName) {
  const secondaryAppName = 'ClinicAdminCreation';
  const existingApp = getApps().find((a) => a.name === secondaryAppName);
  if (existingApp) await deleteApp(existingApp);

  const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    const user = userCredential.user;
    if (displayName) {
      await updateProfile(user, { displayName });
    }
    const uid = user.uid;
    await deleteApp(secondaryApp);
    return { uid };
  } catch (err) {
    await deleteApp(secondaryApp).catch(() => {});
    throw err;
  }
}

export const authService = {
  createAuthUserWithoutSigningIn,

  // Helper function to convert Firebase auth errors to user-friendly messages
  getAuthErrorMessage(error) {
    // Check if it's a Firebase auth error with a code
    if (error.code) {
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          return 'Invalid email/username or password. Please check your credentials and try again.';
        case 'auth/too-many-requests':
          return 'Too many failed login attempts. Please wait a few minutes before trying again.';
        case 'auth/user-disabled':
          return 'This account has been deactivated. Please contact your administrator for assistance.';
        case 'auth/account-exists-with-different-credential':
          return 'An account already exists with this email using a different sign-in method.';
        case 'auth/invalid-email':
          return 'Please enter a valid email address.';
        case 'auth/weak-password':
          return 'Password is too weak. Please choose a stronger password.';
        case 'auth/email-already-in-use':
          return 'An account with this email already exists.';
        case 'auth/operation-not-allowed':
          return 'Email/password sign-in is not enabled. Please contact your administrator.';
        case 'auth/network-request-failed':
          return 'Network error. Please check your internet connection and try again.';
        default:
          return 'Authentication failed. Please try again or contact support if the problem persists.';
      }
    }
    
    // If it's a custom error with a message, return that
    if (error.message) {
      return error.message;
    }
    
    // Fallback error message
    return 'An unexpected error occurred. Please try again.';
  },

  // Register new user
  async register(userData) {
    try {
      // Check if user is already in staff collection
      const q = query(collection(db, 'staff'), where('email', '==', userData.email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error('You are not authorized to register. Contact your clinic administrator.');
      }

      const staffDoc = snapshot.docs[0];
      const staffData = staffDoc.data();

      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );

      const user = userCredential.user;

      // Update display name
      await updateProfile(user, {
        displayName: `${staffData.firstName} ${staffData.lastName}`,
      });

      // Create or update user profile in Firestore
      await setDoc(
        doc(db, 'users', user.uid),
        {
          uid: user.uid,
          email: userData.email,
          displayName: `${staffData.firstName} ${staffData.lastName}`,
          role: staffData.role || 'clinic_staff',
          clinicId: staffData.clinicId || null,
          permissions: {
            canViewPatients: true,
            canEditPatients: false,
            canSendForms: false,
            canManageTemplates: false,
            canManageBilling: false,
            canManageStaff: false,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          isActive: true,
        },
        { merge: true }
      );

      // Mark staff record as registered
      await setDoc(
        staffDoc.ref,
        {
          ...staffData,
          registered: true,
          userId: user.uid,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Log the registration
      await this.logActivity('user.registered', {
        userId: user.uid,
        userEmail: user.email,
      });

      return user;
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(this.getAuthErrorMessage(error));
    }
  },

  async finalizeAuthenticatedSession(user, email, loginMethod = 'email_password') {
      console.log('Firebase authentication successful for user:', user.uid);

      await setDoc(
        doc(db, 'users', user.uid),
        {
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );

      // Update lastLogin in staff collection (non-blocking)
      (async () => {
        try {
          const staffQuery = query(collection(db, 'staff'), where('email', '==', email));
          const staffSnapshot = await getDocs(staffQuery);
          if (!staffSnapshot.empty) {
            const staffDocRef = staffSnapshot.docs[0].ref;
            let ipAddress = 'unknown';
            try {
              ipAddress = await this.getClientIP();
            } catch (ipErr) {
              console.error('Failed to fetch IP address:', ipErr);
            }
            await setDoc(
              staffDocRef,
              {
                lastLogin: {
                  timestamp: serverTimestamp(),
                  ipAddress: ipAddress
                }
              },
              { merge: true }
            );
          }
        } catch (err) {
          console.error('Non-blocking: Failed to update lastLogin in staff document:', err);
        }
      })();

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      console.log('Retrieved user data from Firestore:', userData);

      if (!userData) {
        console.error('No user data found in Firestore');
        await signOut(auth);
        throw new Error('User account not found. Please contact your administrator.');
      }

      // Only block login if user is explicitly deactivated (not based on subscription status)
      if (userData.isActive === false) {
        console.log('User account is explicitly deactivated');
        await signOut(auth);
        throw new Error('Account is deactivated');
      }

      /** Prefer clinics/{id}.name for audit logs — user docs often omit denormalised clinicName. */
      let resolvedClinicNameForLog =
        userData.role === 'super_admin'
          ? userData.clinicName || 'Super Admin Panel'
          : userData.clinicName || null;

      if (userData.clinicId) {
        const clinicDoc = await getDoc(doc(db, 'clinics', userData.clinicId));
        const clinicData = clinicDoc.data() || {};

        if (userData.role !== 'super_admin' && clinicData.name) {
          resolvedClinicNameForLog = clinicData.name;
        }

        // Only block login if clinic is explicitly suspended or deactivated
        // Subscription status (canceled/active) should NOT prevent login
        // Users with canceled subscriptions can still log in, they just can't create consents if expired
        if (clinicData.isActive === false || clinicData.isSuspended === true) {
          console.log('Clinic account is explicitly suspended or deactivated');
          await signOut(auth);
          throw new Error('Clinic account is suspended');
        }
      }

      // Staff row may exist for the same email — never downgrade clinic_admin
      try {
        const staffPermissions = await getStaffPermissions(email);
        if (staffPermissions) {
          console.log('Merging staff record (login hours / clinicId):', staffPermissions);
          Object.assign(userData, mergeStaffRecordFields(userData, staffPermissions));
          applyClinicAdminPrivileges(userData);
          console.log('Final merged user data:', {
            role: userData.role,
            permissions: userData.permissions,
            permissionLevel: userData.permissionLevel,
          });
        }
      } catch (permissionError) {
        console.error('Error getting staff permissions:', permissionError);
      }
      applyClinicAdminPrivileges(userData);

      if (userData.clinicId && userData.role !== 'super_admin') {
        resolvedClinicNameForLog = await resolveClinicNameFromFirestore(
          userData.clinicId,
          resolvedClinicNameForLog || userData.clinicName
        );
      }

      await syncUserClinicToFirestore({ ...userData, uid: user.uid });

      // Set session start time for duration tracking
      this.sessionStartTime = Date.now();
      
      await this.logActivity('user.login', {
        userId: user.uid,
        userEmail: user.email,
        userName: userData.displayName || user.displayName || 'Unknown User',
        userRole: userData.role || 'Unknown Role',
        clinicId: userData.clinicId,
        clinicName:
          userData.role === 'super_admin'
            ? resolvedClinicNameForLog
            : resolvedClinicNameForLog || 'Unknown Clinic',
        loginMethod,
        loginSuccess: true
      });

      console.log('Sign in process completed successfully');
      return { user, userData };
  },

  // Sign in with email/password
  async signIn(email, password) {
    try {
      console.log('Starting sign in process for:', email);

      // Check login restrictions before attempting authentication
      try {
        const restrictionCheck = await checkLoginRestrictions(email);
        if (!restrictionCheck.allowed) {
          throw new Error(restrictionCheck.message);
        }
      } catch (restrictionError) {
        console.error('Error checking login restrictions:', restrictionError);
        // If restriction check fails, still allow login to prevent blocking users
        console.log('Allowing login despite restriction check error');
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return await this.finalizeAuthenticatedSession(userCredential.user, email, 'email_password');
    } catch (error) {
      if (isMfaRequiredError(error)) {
        throw new MfaRequiredError(error);
      }

      console.error('Sign in error:', error);

      // Log failed login attempt for security monitoring
      try {
        await this.logActivity('user.login_failed', {
          userEmail: email,
          errorMessage: error.message,
          loginMethod: 'email_password',
          loginSuccess: false,
          timestamp: new Date().toISOString()
        });
      } catch (logError) {
        console.log('Failed to log failed login attempt:', logError.message);
      }

      // Throw a user-friendly error message instead of the raw Firebase error
      throw new Error(this.getAuthErrorMessage(error));
    }
  },

  async completeMfaSignIn(firebaseError, verificationCode) {
    try {
      const user = await completeTotpSignIn(firebaseError, verificationCode);
      return await this.finalizeAuthenticatedSession(
        user,
        user.email || '',
        'email_password_totp'
      );
    } catch (error) {
      const message = error?.code ? mfaActionErrorMessage(error) : (error?.message || 'Authenticator sign-in failed.');
      try {
        await this.logActivity('user.login_failed', {
          userEmail: auth.currentUser?.email || null,
          errorMessage: message,
          loginMethod: 'email_password_totp',
          loginSuccess: false,
          timestamp: new Date().toISOString()
        });
      } catch (logError) {
        console.log('Failed to log failed authenticator attempt:', logError.message);
      }
      throw new Error(message);
    }
  },

  // Sign in with username/password
  async signInWithUsername(username, password) {
    try {
      console.log('Starting username sign in process for:', username);
      
      const userQuery = query(collection(db, 'users'), where('username', '==', username));
      const snapshot = await getDocs(userQuery);

      if (snapshot.empty) throw new Error('Invalid username or password');

      const userDoc = snapshot.docs[0];
      const userData = userDoc.data();
      console.log('Retrieved user data for username login:', userData);

      if (userData.password !== password) throw new Error('Invalid username or password');

      // Only block login if user is explicitly deactivated (not based on subscription status)
      if (userData.isActive === false) {
        throw new Error('Account is deactivated');
      }

      let resolvedClinicNameForLog = userData.clinicName || null;

      if (userData.clinicId) {
        const clinicDoc = await getDoc(doc(db, 'clinics', userData.clinicId));
        const clinicData = clinicDoc.data() || {};

        if (clinicData.name) {
          resolvedClinicNameForLog = clinicData.name;
        }

        // Only block login if clinic is explicitly suspended or deactivated
        // Subscription status (canceled/active) should NOT prevent login
        if (clinicData.isActive === false || clinicData.isSuspended === true) {
          throw new Error('Clinic account is suspended');
        }
      }

      // Check login restrictions for username-based login
      if (userData.email) {
        try {
          const restrictionCheck = await checkLoginRestrictions(userData.email);
          if (!restrictionCheck.allowed) {
            throw new Error(restrictionCheck.message);
          }
        } catch (restrictionError) {
          console.error('Error checking login restrictions for username login:', restrictionError);
          // If restriction check fails, still allow login to prevent blocking users
          console.log('Allowing username login despite restriction check error');
        }
      }

      await setDoc(
        doc(db, 'users', userDoc.id),
        { lastLogin: serverTimestamp() },
        { merge: true }
      );

      // Get staff permissions and merge with user data
      if (userData.email) {
        try {
          const staffPermissions = await getStaffPermissions(userData.email);
          if (staffPermissions) {
            console.log('Merging staff permissions for username login:', staffPermissions);
            Object.assign(userData, mergeStaffRecordFields(userData, staffPermissions));
            applyClinicAdminPrivileges(userData);
          }
        } catch (permissionError) {
          console.error('Error getting staff permissions for username login:', permissionError);
          // Continue without staff permissions if there's an error
        }
      }

      if (!userData.clinicId) {
        try {
          const staffByUidQuery = query(collection(db, 'staff'), where('userId', '==', userDoc.id));
          const staffByUidSnapshot = await getDocs(staffByUidQuery);
          if (!staffByUidSnapshot.empty) {
            const staffRow = staffByUidSnapshot.docs[0].data();
            if (staffRow.clinicId) {
              userData.clinicId = staffRow.clinicId;
              userData.clinicName = userData.clinicName || staffRow.clinicName || null;
            }
          }
        } catch (staffLookupError) {
          console.error('Error looking up staff by userId for username login:', staffLookupError);
        }
      }

      if (userData.clinicId) {
        resolvedClinicNameForLog = await resolveClinicNameFromFirestore(
          userData.clinicId,
          userData.clinicName || resolvedClinicNameForLog
        );
      }

      applyClinicAdminPrivileges(userData);
      await syncUserClinicToFirestore({ ...userData, uid: userDoc.id });

      // Set session start time for duration tracking
      this.sessionStartTime = Date.now();
      
      await this.logActivity('user.login', {
        userId: userDoc.id,
        username: username,
        userEmail: userData.email || 'No email',
        userName: userData.displayName || 'Unknown User',
        userRole: userData.role || 'Unknown Role',
        clinicId: userData.clinicId || null,
        clinicName: resolvedClinicNameForLog || 'Unknown Clinic',
        loginMethod: 'username_password',
        loginSuccess: true
      });

      const mockUser = {
        uid: userDoc.id,
        email: userData.email,
        displayName: userData.displayName,
      };

      console.log('Username sign in process completed successfully');
      return { user: mockUser, userData: { ...userData, uid: userDoc.id } };
    } catch (error) {
      console.error('Username sign in error:', error);
      
      // Log failed login attempt for security monitoring
      try {
        await this.logActivity('user.login_failed', {
          username: username,
          userEmail: 'No email', // userData not available in failed login scope
          errorMessage: error.message,
          loginMethod: 'username_password',
          loginSuccess: false,
          timestamp: new Date().toISOString()
        });
      } catch (logError) {
        console.log('Failed to log failed login attempt:', logError.message);
      }
      
      throw new Error(this.getAuthErrorMessage(error));
    }
  },

  // Enhanced Sign out with rich context logging
  async signOut() {
    try {
      const user = auth.currentUser;
      let userData = null;
      let clinicName = null;
      
      // Capture user context before signing out
      if (user) {
        try {
          // Get user data for rich logout logging
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            userData = userDoc.data();
            
            // Get clinic information if available
            if (userData.clinicId) {
              try {
                const clinicDoc = await getDoc(doc(db, 'clinics', userData.clinicId));
                if (clinicDoc.exists()) {
                  const clinicData = clinicDoc.data();
                  clinicName = clinicData.name;
                }
              } catch (err) {
                console.log('Could not fetch clinic data for logout logging:', err.message);
              }
            }
          }
        } catch (err) {
          console.log('Could not fetch user data for logout logging:', err.message);
        }

        // Log logout with rich context
        await this.logActivity('user.logout', {
          userId: user.uid,
          userEmail: user.email,
          userName: userData?.displayName || user.displayName || 'Unknown User',
          userRole: userData?.role || 'Unknown Role',
          clinicId: userData?.clinicId || null,
          clinicName: clinicName || 'Unknown Clinic',
          sessionDuration: this.calculateSessionDuration(),
          logoutReason: 'user_initiated'
        });
      }
      
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw new Error(this.getAuthErrorMessage(error));
    }
  },

  // Calculate session duration for logout logging
  calculateSessionDuration() {
    if (this.sessionStartTime) {
      const duration = Date.now() - this.sessionStartTime;
      const minutes = Math.floor(duration / 60000);
      const seconds = Math.floor((duration % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
    return 'Unknown';
  },

  // Password reset
  async resetPassword(email) {
    try {
      const normalizedEmail = (email || '').trim();
      if (!normalizedEmail) throw new Error('Email is required');

      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://smileconsent.co.uk';

      /** Branded email path actually used (for audit / debugging). */
      let usedBackend = false;
      let channel = 'none';

      // 1) Primary: Cloud Function `sendPasswordReset` (Nodemailer + branded HTML).
      //    Firebase Hosting rewrites ALL paths to index.html, so POST /api/send-password-reset
      //    often returns 200 HTML and never sends mail — do not rely on that without JSON checks.
      try {
        const sendPasswordReset = httpsCallable(functions, 'sendPasswordReset');
        const response = await sendPasswordReset({
          email: normalizedEmail,
          baseUrl: origin,
        });
        if (response?.data?.success) {
          usedBackend = true;
          channel = 'cloud_function';
        }
      } catch (cfErr) {
        console.warn('[authService.resetPassword] sendPasswordReset callable failed:', cfErr?.message || cfErr);
      }

      // 2) Optional: Node/Express `/api/send-password-reset` (EmailJS) — only if JSON says success.
      if (!usedBackend) {
        try {
          const resp = await fetch('/api/send-password-reset', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: normalizedEmail }),
          });
          const ct = (resp.headers.get('content-type') || '').toLowerCase();
          if (resp.ok && ct.includes('application/json')) {
            const data = await resp.json();
            if (data?.success === true) {
              usedBackend = true;
              channel = 'express_emailjs';
            }
          }
        } catch (fetchErr) {
          console.warn('[authService.resetPassword] /api/send-password-reset:', fetchErr?.message || fetchErr);
        }
      }

      // 3) Fallback: Firebase Auth default reset email (works everywhere; template is less pretty).
      if (!usedBackend) {
        await sendPasswordResetEmail(auth, normalizedEmail, {
          url: `${origin}/reset-password`,
        });
        channel = 'firebase_client';
      }

      await this.logActivity('user.password_reset_requested', {
        email: normalizedEmail,
        usedBackend: channel !== 'firebase_client',
        channel,
      });
      return { usedBackend: channel !== 'firebase_client', channel };
    } catch (error) {
      console.error('Password reset error:', error);
      throw new Error(this.getAuthErrorMessage(error));
    }
  },

  // Get current user
  async getCurrentUserData() {
    try {
      const user = auth.currentUser;

      if (user) {
        if (user.email === 'admin@smartconsent.com') {
          return {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || 'Super Admin',
            role: 'super_admin',
            isActive: true,
          };
        }

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const merged = { ...data, uid: data.uid || user.uid };
          if (user.email) merged.email = user.email;
          if (user.displayName && !merged.displayName) merged.displayName = user.displayName;
          return merged;
        }
        return null;
      }

      const storedUserData = localStorage.getItem('clinic_user_data');
      if (storedUserData) return JSON.parse(storedUserData);

      return null;
    } catch (error) {
      console.error('Error in getCurrentUserData:', error);

      const fallbackUser = auth.currentUser;
      if (fallbackUser?.email === 'admin@smartconsent.com') {
        return {
          uid: fallbackUser.uid,
          email: fallbackUser.email,
          displayName: fallbackUser.displayName || 'Super Admin',
          role: 'super_admin',
          isActive: true,
        };
      }

      const storedUserData = localStorage.getItem('clinic_user_data');
      if (storedUserData) return JSON.parse(storedUserData);

      return null;
    }
  },

  // Auth state listener
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Enhanced Activity logging with rich user context
  async logActivity(action, details) {
    try {
      const user = auth.currentUser;
      let userData = null;
      
      // Try to get current user data for richer logging
      try {
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            userData = userDoc.data();
          }
        }
      } catch (err) {
        console.log('Could not fetch user data for logging:', err.message);
      }

      let ipAddress = 'unknown';
      try {
        ipAddress = await this.getClientIP();
      } catch (ipErr) {
        console.error('Failed to fetch IP address:', ipErr);
      }

      // Get clinic information if available (practiceId is superadmin naming for clinic id)
      const resolvedClinicId = details.clinicId || details.practiceId || userData?.clinicId;
      let clinicName = details.clinicName || details.practiceName || null;
      // Placeholder from callers must not skip Firestore lookup when we have a clinic id
      if (clinicName === 'Unknown Clinic' && resolvedClinicId) {
        clinicName = null;
      }
      let clinicData = null;
      if (resolvedClinicId && !clinicName) {
        try {
          const clinicDoc = await getDoc(doc(db, 'clinics', resolvedClinicId));
          if (clinicDoc.exists()) {
            clinicData = clinicDoc.data();
            clinicName = clinicData.name;
          }
        } catch (err) {
          console.log('Could not fetch clinic data for logging:', err.message);
        }
      }

      const trimmedDetailName =
        details.userName != null && String(details.userName).trim() !== ''
          ? String(details.userName).trim()
          : null;
      const resolvedActorName =
        trimmedDetailName ||
        userData?.displayName ||
        user?.displayName ||
        user?.email ||
        userData?.email ||
        (details.userEmail != null && String(details.userEmail).trim() !== ''
          ? String(details.userEmail).trim()
          : null) ||
        (details.email != null && String(details.email).trim() !== '' ? String(details.email).trim() : null) ||
        null;

      // Enhanced details object with user context
      const enhancedDetails = {
        ...details,
        userName: resolvedActorName || 'Unknown User',
        userRole: userData?.role || details.userRole || 'Unknown Role',
        clinicName: clinicName || details.clinicName || details.practiceName || 'Unknown Clinic',
        clinicId: resolvedClinicId || null,
        userEmail:
          user?.email ||
          userData?.email ||
          details.userEmail ||
          details.email ||
          'Unknown Email',
        timestamp: new Date().toISOString(),
        sessionId: this.generateSessionId()
      };

      await setDoc(doc(db, 'auditLogs', `${Date.now()}_${Math.random()}`), {
        clinicId: enhancedDetails.clinicId,
        userId: user?.uid || details.userId || null,
        userEmail: enhancedDetails.userEmail,
        userName: enhancedDetails.userName,
        userRole: enhancedDetails.userRole,
        action: action,
        details: enhancedDetails,
        timestamp: serverTimestamp(),
        ipAddress: ipAddress,
        userAgent: navigator.userAgent,
        sessionId: enhancedDetails.sessionId,
        // Additional context for better tracking
        browser: this.getBrowserInfo(),
        platform: navigator.platform,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }).catch((err) => {
        console.log('Failed to log activity:', err.message);
      });

      console.log(`🔍 Activity logged: ${action}`, enhancedDetails);
    } catch (error) {
      console.log('Activity logging failed:', error.message);
    }
  },

  // Generate unique session ID for tracking user sessions
  generateSessionId() {
    if (!this.currentSessionId) {
      this.currentSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.currentSessionId;
  },

  // Get browser information
  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let version = 'Unknown';
    
    if (userAgent.includes('Chrome')) {
      browser = 'Chrome';
      version = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
      version = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('Safari')) {
      browser = 'Safari';
      version = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('Edge')) {
      browser = 'Edge';
      version = userAgent.match(/Edge\/(\d+)/)?.[1] || 'Unknown';
    }
    
    return { name: browser, version: version };
  },

  // Client IP (placeholder)
  async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  },

  getImpersonationSession() {
    if (typeof sessionStorage === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.clinicId) return null;
      return parsed;
    } catch {
      return null;
    }
  },

  clearImpersonationSession() {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
    } catch {
      /* ignore */
    }
  },

  /**
   * Superadmin support: mint a custom token for the clinic admin and switch the
   * current Firebase session to that user (replaces the superadmin session).
   */
  async impersonateClinicAdmin({ clinicId, clinicName, returnPath } = {}) {
    const id = typeof clinicId === 'string' ? clinicId.trim() : '';
    if (!id) throw new Error('clinicId is required');
    if (!auth.currentUser) throw new Error('You must be signed in as a super administrator.');

    const actorUid = auth.currentUser.uid;
    const actorEmail = auth.currentUser.email || null;

    try {
      const getToken = httpsCallable(functions, 'getClinicAdminImpersonationToken');
      const response = await getToken({ clinicId: id });
      const customToken = response?.data?.customToken;
      const targetUid = response?.data?.targetUid;
      if (!customToken) {
        throw new Error('No login token was returned for this clinic.');
      }

      try {
        await this.logActivity('user.impersonation_start', {
          clinicId: id,
          clinicName: clinicName || null,
          targetUid: targetUid || null,
          actorUid,
          actorEmail,
        });
      } catch (_) {
        /* non-blocking */
      }

      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(
          IMPERSONATION_SESSION_KEY,
          JSON.stringify({
            clinicId: id,
            clinicName: clinicName || null,
            returnPath: returnPath || `/admin/clinics/${id}`,
            targetUid: targetUid || null,
            actorUid,
            actorEmail,
            startedAt: Date.now(),
          })
        );
      }

      await signInWithCustomToken(auth, customToken);
      this.sessionStartTime = Date.now();

      try {
        await this.logActivity('user.impersonation_active', {
          clinicId: id,
          clinicName: clinicName || null,
          targetUid: targetUid || null,
          actorUid,
          actorEmail,
        });
      } catch (_) {
        /* non-blocking */
      }

      return { targetUid, clinicId: id };
    } catch (error) {
      this.clearImpersonationSession();
      const message = getCallableErrorMessage(error) || error?.message || 'Failed to open clinic portal.';
      throw new Error(message);
    }
  },

  async endImpersonation() {
    this.clearImpersonationSession();
    await this.signOut();
  },
};

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.authService = authService;
}
